// tekno3 CourseOps (spec 2026-10-10 §3) → the editor store as ONE undo step. Ops arrive in px on
// 1920×1080 and seconds (mw course-ops.ts); the store keeps % (3 decimals) and frames at FPS — the
// same math as mw scene-doc. Each op is checked against the live state right before it runs: a bad op
// lands in `errors` and is skipped, the others still apply. Only the reviewed scene (`navId`) is touched.
import { getDescendantBoxes } from "../../common/utils";
import { createBox } from "../../common/common_tools";
import {
  addBox,
  changeBoxLayer,
  changeNavItemDuration,
  deleteBox,
  deleteBoxAnimation,
  deleteBoxExitAnimation,
  moveBox,
  resizeBox,
  updateBox,
  updateBoxAnimation,
  updateBoxExitAnimation,
  updateBoxTimeline,
  updatePluginToolbar,
  updateViewToolbar,
} from "../../common/actions";
import {
  getBoxAnimationPresets,
  getBoxExitAnimationPresets,
  normalizeAnimationName,
} from "./animationHelpers";
import { FPS } from "../../common/sceneTimeline.es6";
import { ID_PREFIX_BOX } from "../../common/constants";
import { isSafeFontName } from "../../common/font_extractor.es6";
import {
  CANVAS_H,
  CANVAS_W,
  PILL_MAX_RADIUS_PX,
  PX_PER_EM,
  fitTextToBox,
  textHtml,
} from "./motionImporter";

// mw scene-doc removeBox: a box an agent made (-agt- / -mdir-) is deleted, the user's own is only hidden.
const AGENT_BOX = /-(agt|mdir)-/;
const HEX = /^#[0-9a-fA-F]{3,8}$/;
const HTTPS = /^https:\/\/[^\s()"'<>]+$/;
const MAX_CONTENT = 5000;
const SVG_DATA = /^data:image\/svg\+xml[;,]/i;
// mw plan-boxes GLYPH_EM: average glyph advance in em; first match wins, else 0.5.
const GLYPH_EM = [
  [/^geist/i, 0.47],
  [/^cormorant/i, 0.4],
  [/serif|zodiak|garamond|playfair|georgia|times|lora|merriweather/i, 0.45],
];
// ponytail: mw fits text on a glyph estimate ×1.10 while motionImporter's fitTextToBox multiplies by
// 1.03 — scaling the estimate makes both sides land on the same box (the contract fixture pins it).
const MW_FIT = 1.1 / 1.03;
const BOLD_SLACK = 1.12;
const PILL_MAX_CHARS = 24;
const ENTITIES = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&nbsp;": " ",
};
let seq = 0;

const pct = (v, base) => +((Number(v) / base) * 100).toFixed(3);
const fr = (sec) => Math.round(Number(sec) * FPS);
const fin = (n) => typeof n === "number" && Number.isFinite(n);
const fail = (message) => {
  throw new Error(message);
};

// Last value of a css property across the given declarations ("a:1;b:2;a:3" → "3").
function lastDecl(decls, name) {
  const re = new RegExp(`(?:^|[;\\s])${name}\\s*:\\s*([^;]+)`, "gi");
  let m;
  let value = null;
  while ((m = re.exec(decls))) value = m[1].trim();
  return value;
}

/**
 * The text style of a BasicText `__text`; em sizes use the canvas PX_PER_EM. Reporting (default):
 * the LAST declaration across all style="…" attributes wins (an inner <span> overrides its <p>).
 * `{ outer: true }`: only the outer <p> declaration — the base update_text re-renders from (G6).
 */
export function parseTextStyle(html, { outer = false } = {}) {
  const s = String(html || "");
  const attrs = outer ? (s.match(/^\s*<p\b[^>]*>/i) || [""])[0] : s;
  const decls = (attrs.match(/style\s*=\s*"[^"]*"/gi) || [])
    .map((a) => a.slice(a.indexOf('"') + 1, -1))
    .join(";");
  const size = lastDecl(decls, "font-size");
  const family = lastDecl(decls, "font-family");
  const px = !size
    ? NaN
    : /em$/i.test(size)
      ? parseFloat(size) * PX_PER_EM
      : /px$/i.test(size)
        ? parseFloat(size)
        : NaN;
  return {
    content: s
      .replace(/<\/p>\s*<p[^>]*>|<br\s*\/?>/gi, " ")
      .replace(/<[^>]*>/g, "")
      .replace(/&(amp|lt|gt|quot|#39|nbsp);/g, (e) => ENTITIES[e])
      .trim(),
    color: lastDecl(decls, "color"),
    fontFamily: family ? family.split(",")[0].replace(/['"]/g, "").trim() : null,
    fontSizePx: Number.isFinite(px) ? Math.round(px) : null,
    weight: Number(lastDecl(decls, "font-weight")) || null,
    align: lastDecl(decls, "text-align"),
    wrap: lastDecl(decls, "white-space") === "normal",
  };
}

const fromPos = (v, base) =>
  typeof v === "string" && /%\s*$/.test(v) ? +((parseFloat(v) / 100) * base).toFixed(1) : null;
const fromSize = (v, unit, base) =>
  unit === "%" && v !== "auto" && v != null && Number.isFinite(Number(v))
    ? +((Number(v) / 100) * base).toFixed(1)
    : null;

/** Box geometry in px on 1920×1080 (1 decimal); null where the box is not in %. */
export function boxRect(present, id) {
  const b = (present.boxesById || {})[id] || {};
  const s = ((present.pluginToolbarsById || {})[id] || {}).structure || {};
  return {
    x: fromPos(b.position && b.position.x, CANVAS_W),
    y: fromPos(b.position && b.position.y, CANVAS_H),
    w: fromSize(s.width, s.widthUnit, CANVAS_W),
    h: fromSize(s.height, s.heightUnit, CANVAS_H),
  };
}

function estimateWidth(el) {
  const family = isSafeFontName(el.text.font) ? el.text.font : "Geist";
  const em = (GLYPH_EM.find(([re]) => re.test(family)) || [null, 0.5])[1];
  const text = (el.text.runs || []).map((r) => r.text).join("");
  return (
    text.length * el.text.size_px * em * MW_FIT * (Number(el.text.weight) >= 700 ? BOLD_SLACK : 1)
  );
}

// TextStyle → plan text element → fitted box + editor html (mw update_text / addTextBox rules, G6).
function renderText(rect, content, st, keepW = false) {
  const source = {
    type: "text",
    x: rect.x,
    y: rect.y,
    w: rect.w,
    h: rect.h,
    text: {
      runs: [{ text: content }],
      font: st.fontFamily || "Geist",
      size_px: st.fontSizePx || 48,
      weight: st.weight || undefined,
      color: st.color || undefined,
      align: st.align === "center" ? "center" : "left",
      wrap: !!st.wrap,
      ...(st.background ? { container: { bg: st.background } } : {}),
    },
  };
  // ponytail: a box not in % (legacy px boxes) keeps its size — no fit.
  const el = rect.x == null || rect.w == null ? source : fitTextToBox(source, estimateWidth, keepW);
  const html = textHtml(el, null);
  return {
    el,
    html: st.lineHeight
      ? html.replace(/line-height:[^;]+;/, `line-height:${st.lineHeight};`)
      : html,
  };
}

// Set one css property in a declaration list: replace every occurrence, else append.
function setDecl(decls, name, value) {
  const re = new RegExp(`(^|[;\\s])${name}\\s*:[^;]*`, "gi");
  if (decls.search(re) >= 0) return decls.replace(re, (_, pre) => `${pre}${name}:${value}`);
  const base = decls.trim().replace(/;$/, "");
  return base ? `${base};${name}:${value}` : `${name}:${value}`;
}
// Patch the outer <p> style(s) only; the inner runs (<span> colours, italic, strike…) stay as they are.
function patchParagraphs(html, decls) {
  return html.replace(/<p\b[^>]*>/gi, (tag) => {
    const m = tag.match(/style\s*=\s*"([^"]*)"/i);
    const style = Object.entries(decls).reduce((acc, [k, v]) => setDecl(acc, k, v), m ? m[1] : "");
    return m
      ? tag.replace(m[0], () => `style="${style}"`)
      : tag.replace(/^<p/i, () => `<p style="${style}"`);
  });
}
const CSS_OF = {
  color: "color",
  fontFamily: "font-family",
  weight: "font-weight",
  align: "text-align",
  lineHeight: "line-height",
};

const assertPillLabel = (content) => {
  if (content.length > PILL_MAX_CHARS) {
    fail(`pills are for short labels (≤${PILL_MAX_CHARS} chars); use add_scrim behind longer text`);
  }
};

// Radius is a "px" string: a bare number would render as value/7 em.
const pillStyle = (bg, hPx) =>
  bg
    ? {
        backgroundColor: bg,
        borderWidth: 1,
        borderStyle: "solid",
        borderColor: bg,
        borderRadius: `${Math.min(PILL_MAX_RADIUS_PX, (hPx == null ? 2 * PILL_MAX_RADIUS_PX : hPx) / 2)}px`,
      }
    : { backgroundColor: "rgba(255,255,255,0)", borderWidth: 0 };

function currentBackground(toolbar) {
  const c = toolbar && toolbar.style && toolbar.style.backgroundColor;
  return typeof c === "string" && HEX.test(c) ? c : null;
}

// TextStyle values land in style attributes — validated before anything is dispatched.
function checkStyle(st) {
  if (st.color !== undefined && !HEX.test(st.color)) fail("invalid color");
  if (st.background != null && !HEX.test(st.background)) fail("invalid background");
  if (st.fontFamily !== undefined && !isSafeFontName(st.fontFamily)) fail("invalid fontFamily");
  if (
    st.fontSizePx !== undefined &&
    !(fin(st.fontSizePx) && st.fontSizePx >= 24 && st.fontSizePx <= 160)
  )
    fail("invalid fontSizePx");
  if (
    st.lineHeight !== undefined &&
    !(fin(st.lineHeight) && st.lineHeight >= 1 && st.lineHeight <= 1.6)
  )
    fail("invalid lineHeight");
  if (
    st.weight !== undefined &&
    !(Number.isInteger(st.weight) && st.weight >= 100 && st.weight <= 900 && st.weight % 100 === 0)
  ) {
    fail("invalid weight");
  }
  // textHtml renders center or left only.
  if (st.align !== undefined && st.align !== "left" && st.align !== "center") fail("invalid align");
  return st;
}

// Sane geometry: positive size, position within one canvas of the frame.
function checkGeom(op) {
  if (op.x != null && (op.x < -CANVAS_W || op.x > 2 * CANVAS_W)) fail("x out of range");
  if (op.y != null && (op.y < -CANVAS_H || op.y > 2 * CANVAS_H)) fail("y out of range");
  if (op.w != null && !(op.w > 0)) fail("invalid w");
  if (op.h != null && !(op.h > 0)) fail("invalid h");
}
const checkContent = (c) =>
  typeof c === "string" && c.length <= MAX_CONTENT ? c : fail("invalid content");

function preset(list, a) {
  const name = normalizeAnimationName(a && a.name);
  return (
    list.find((p) => p.name === name) ||
    fail(`unknown animation ${String(a && a.name).slice(0, 40)}`)
  );
}
// mw AnimSpec: undefined = leave as is, null = remove, { name, durationSec?, delaySec? } = set (G2).
function resolveAnim(enter, exit) {
  const pick = (list, a, dflt) => {
    if (a == null) return a;
    if (
      a.durationSec !== undefined &&
      !(fin(a.durationSec) && a.durationSec >= 0 && a.durationSec <= 10)
    )
      fail("invalid animation duration");
    return {
      p: preset(list, a),
      duration: fin(a.durationSec) ? a.durationSec : dflt,
      delay: fin(a.delaySec) ? a.delaySec : 0,
    };
  };
  return {
    enter: pick(getBoxAnimationPresets(), enter, 0.6),
    exit: pick(getBoxExitAnimationPresets(), exit, 0.5),
  };
}
function applyAnim(dispatch, id, { enter, exit }) {
  if (enter === null) dispatch(deleteBoxAnimation(id));
  else if (enter) dispatch(updateBoxAnimation(id, enter.p, enter.duration, enter.delay));
  if (exit === null) dispatch(deleteBoxExitAnimation(id));
  else if (exit) dispatch(updateBoxExitAnimation(id, exit.p, exit.duration));
}

// mw scene-doc timeline rules: frames ≥ 0, end > start, end ≤ the scene's duration.
function timelineFor(nav, current, start, end) {
  const tl = { startFrame: 0, endFrame: 1, ...(current || {}) };
  if (start !== undefined) tl.startFrame = Math.max(0, fr(start));
  if (end !== undefined) tl.endFrame = fr(end);
  if (tl.endFrame <= tl.startFrame) tl.endFrame = tl.startFrame + 1;
  if (nav.duration > 0) tl.endFrame = Math.min(tl.endFrame, nav.duration);
  return { startFrame: tl.startFrame, endFrame: tl.endFrame };
}

function sceneBox(present, navId, boxId) {
  const box = present.boxesById && present.boxesById[boxId];
  if (!box || box.parent !== navId) fail(`unknown box ${String(boxId).slice(0, 80)}`);
  return box;
}
function sameScene(op, navId) {
  if (op.navId !== navId) fail("op is for another scene");
}

// update_text; update_box re-runs it (keepW) after a resize so the font shrinks instead of clipping.
function updateText(dispatch, present, navId, op, keepW) {
  const box = sceneBox(present, navId, op.boxId);
  const tb = present.pluginToolbarsById && present.pluginToolbarsById[op.boxId];
  if (!tb || tb.pluginId !== "BasicText") fail("not a text box");
  if (!box.position) fail("box has no position");
  if (op.content !== undefined) checkContent(op.content);
  const want = checkStyle(op.style || {});
  const old = String((tb.state && tb.state.__text) || "");
  // The re-render base is the outer <p> declaration, not the innermost run (G6).
  const cur = parseTextStyle(old, { outer: true });
  const st = { ...cur, background: currentBackground(tb), ...want };
  const rect = boxRect(present, op.boxId);
  const next = op.content !== undefined ? op.content : cur.content;
  if (st.background && (want.background || op.content !== undefined)) assertPillLabel(next);
  const { el, html } = renderText(rect, next, st, keepW);
  let text = html;
  if (op.content === undefined && /<p\b/i.test(old)) {
    // Style only: patch what changed onto the outer <p>, with the values textHtml rendered.
    const rendered = (html.match(/style="([^"]*)"/) || ["", ""])[1];
    const names = Object.keys(want)
      .filter((k) => CSS_OF[k])
      .map((k) => CSS_OF[k]);
    if (want.fontSizePx !== undefined || el.text.size_px !== (st.fontSizePx || 48))
      names.push("font-size");
    if (el.text.wrap !== !!st.wrap) names.push("white-space");
    if (want.background !== undefined) names.push("text-shadow", "line-height", "padding");
    text = patchParagraphs(
      old,
      Object.fromEntries(
        names.map((n) => [n, lastDecl(rendered, n) ?? (n === "padding" ? "0" : null)])
      )
    );
  }
  dispatch(updateBox(op.boxId, box.content, tb, { ...tb.state, __text: text }));
  if (el.x !== rect.x || el.y !== rect.y) {
    const y = el.y === rect.y ? box.position.y : `${pct(el.y, CANVAS_H)}%`;
    dispatch(
      moveBox(op.boxId, `${pct(el.x, CANVAS_W)}%`, y, "absolute", box.parent, box.container)
    );
  }
  if (el.w !== rect.w || el.h !== rect.h) {
    dispatch(
      resizeBox(op.boxId, {
        width: pct(el.w, CANVAS_W),
        widthUnit: "%",
        height: el.h == null ? "auto" : pct(el.h, CANVAS_H),
        heightUnit: "%",
      })
    );
  }
  if (want.background !== undefined || st.background) {
    const style = pillStyle(st.background || null, el.h);
    dispatch(
      updatePluginToolbar(op.boxId, "main", "style", Object.keys(style), Object.values(style))
    );
  }
  return [op.boxId];
}

function applyOne(dispatch, getState, op, navId) {
  const present = getState().undoGroup.present;
  const nav = present.navItemsById[navId];
  const kind = op.op || op.type;
  switch (kind) {
    case "update_box": {
      const box = sceneBox(present, navId, op.boxId);
      ["x", "y", "w", "h", "start", "end"].forEach((k) => {
        if (op[k] !== undefined && !fin(op[k])) fail(`invalid ${k}`);
      });
      checkGeom(op);
      const anim = resolveAnim(op.enter, op.exit);
      if (op.x !== undefined || op.y !== undefined) {
        const pos = box.position || {};
        dispatch(
          moveBox(
            op.boxId,
            op.x !== undefined ? `${pct(op.x, CANVAS_W)}%` : pos.x,
            op.y !== undefined ? `${pct(op.y, CANVAS_H)}%` : pos.y,
            "absolute",
            box.parent,
            box.container
          )
        );
      }
      const size = {};
      if (op.w !== undefined) Object.assign(size, { width: pct(op.w, CANVAS_W), widthUnit: "%" });
      if (op.h !== undefined) Object.assign(size, { height: pct(op.h, CANVAS_H), heightUnit: "%" });
      if (Object.keys(size).length) dispatch(resizeBox(op.boxId, size));
      if (op.start !== undefined || op.end !== undefined) {
        dispatch(updateBoxTimeline(op.boxId, timelineFor(nav, box.timeline, op.start, op.end)));
      }
      applyAnim(dispatch, op.boxId, anim);
      if (
        (op.w !== undefined || op.h !== undefined) &&
        getState().undoGroup.present.pluginToolbarsById[op.boxId]?.pluginId === "BasicText"
      ) {
        updateText(dispatch, getState().undoGroup.present, navId, { boxId: op.boxId }, true);
      }
      return [op.boxId];
    }
    case "update_text":
      return updateText(dispatch, present, navId, op, false);
    case "set_background": {
      sameScene(op, navId);
      if (op.color !== undefined && !HEX.test(op.color)) fail("invalid color");
      if (op.imageUrl !== undefined && !HTTPS.test(op.imageUrl)) fail("invalid imageUrl");
      const background = op.imageUrl || op.color || fail("empty background");
      // The canvas paints viewToolbarsById[navId].background (G5), not navItem.background.
      dispatch(updateViewToolbar(navId, { background, backgroundAttr: "full" }));
      return [navId];
    }
    case "reorder_box": {
      sceneBox(present, navId, op.boxId);
      if (!fin(op.z)) fail("invalid z");
      const rest = nav.boxes.filter((id) => id !== op.boxId);
      rest.splice(Math.max(0, Math.min(op.z, rest.length)), 0, op.boxId); // splice truncates, as mw reorderBox
      dispatch(changeBoxLayer(op.boxId, navId, 0, "reorder", rest));
      return [op.boxId];
    }
    case "delete_box": {
      const box = sceneBox(present, navId, op.boxId);
      if (!AGENT_BOX.test(op.boxId)) {
        dispatch(updateBoxTimeline(op.boxId, { visible: false }));
        return [op.boxId];
      }
      const boxes = present.boxesById;
      const descendants = getDescendantBoxes(box, boxes);
      const cvs = [op.boxId, ...descendants].reduce(
        (acc, id) => acc.concat((boxes[id] && boxes[id].containedViews) || []),
        []
      );
      dispatch(deleteBox(op.boxId, box.parent, box.container, descendants, cvs, navId));
      return [op.boxId];
    }
    case "set_scene_duration": {
      sameScene(op, navId);
      if (!fin(op.durationSec) || op.durationSec <= 0) fail("invalid durationSec");
      const end = fr(op.durationSec);
      dispatch(changeNavItemDuration(navId, end));
      nav.boxes.forEach((id) => {
        const tl = present.boxesById[id] && present.boxesById[id].timeline;
        if (tl && tl.endFrame > end) dispatch(updateBoxTimeline(id, { endFrame: end }));
      });
      return [navId];
    }
    case "add_box": {
      sameScene(op, navId);
      ["x", "y", "w", "start", "end"].forEach((k) => {
        if (!fin(op[k])) fail(`invalid ${k}`);
      });
      if (op.h != null && !fin(op.h)) fail("invalid h");
      checkGeom(op);
      const anim = resolveAnim(op.enter, op.exit);
      const id = `${ID_PREFIX_BOX}${Date.now()}${String(seq++ % 1000).padStart(3, "0")}-agt-${op.kind}`;
      let rect = { x: op.x, y: op.y, w: op.w, h: op.h == null ? null : op.h };
      let initialState;
      let plugin;
      let style = {};
      if (op.kind === "text") {
        if (!op.text || typeof op.text.content !== "string") fail("text needs content");
        checkContent(op.text.content);
        const st = checkStyle(op.text.style || {});
        if (st.background) assertPillLabel(op.text.content);
        const { el, html } = renderText(rect, op.text.content, st);
        rect = { x: el.x, y: el.y, w: el.w, h: el.h };
        initialState = { __text: html };
        plugin = "BasicText";
        style = st.background ? pillStyle(st.background, el.h) : { padding: 0 };
      } else if (op.kind === "image") {
        if (!HTTPS.test(op.url || "") && !SVG_DATA.test(op.url || ""))
          fail("image needs an https url");
        initialState = { url: op.url, allowDeformed: true };
        plugin = "HotspotImages";
      } else if (op.kind === "video") {
        if (!HTTPS.test(op.url || "")) fail("video needs an https url");
        // mw addVideoBox: a muted clip inside the scene, not a player the learner drives.
        initialState = { url: op.url, controls: false, autoPlay: true, volume: 0, loop: false };
        plugin = "EnrichedPlayer";
      } else {
        // html needs the mw lint (lintHtmlElement) — never applied client-side (G9).
        fail(`add_box ${String(op.kind).slice(0, 20)} is not supported in the studio`);
      }
      const ids = {
        parent: navId,
        container: 0,
        page: navId,
        id,
        position: {
          x: `${pct(rect.x, CANVAS_W)}%`,
          y: `${pct(rect.y, CANVAS_H)}%`,
          type: "absolute",
        },
        initialState,
      };
      // Plain addBox (not addBoxThunk): the op sets its own timeline (applyMotionPlan does the same).
      createBox(
        ids,
        plugin,
        true,
        (...args) => dispatch(addBox(...args)),
        present.boxesById,
        style
      );
      if (!getState().undoGroup.present.boxesById[id]) fail(`${plugin} is not available`);
      dispatch(
        resizeBox(id, {
          width: pct(rect.w, CANVAS_W),
          height: rect.h == null ? "auto" : pct(rect.h, CANVAS_H),
          widthUnit: "%",
          heightUnit: "%",
          aspectRatio: false,
        })
      );
      dispatch(updateBoxTimeline(id, timelineFor(nav, null, op.start, op.end)));
      applyAnim(dispatch, id, { enter: anim.enter || undefined, exit: anim.exit || undefined });
      return [id];
    }
    default:
      // add_scene / delete_scene / set_narration: scene-level, never proposed by /review (G9).
      return fail(`${String(kind).slice(0, 40)} is not supported in the studio`);
  }
}

/**
 * @param {Function} dispatch  editor store dispatch
 * @param {Function} getState  editor store getState
 * @param {Array} ops          CourseOps from mw (px on 1920×1080, seconds)
 * @param {{ navId: string }} target  the reviewed scene — ops on any other scene are refused
 * @returns {{ applied: {index:number, ids:string[]}[], errors: {index:number, message:string}[] }}
 */
export function applyCourseOps(dispatch, getState, ops, { navId }) {
  const list = Array.isArray(ops) ? ops : [];
  const present = getState().undoGroup.present;
  if (!present.navItemsById || !present.navItemsById[navId]) {
    return { applied: [], errors: list.map((_, index) => ({ index, message: "scene-missing" })) };
  }
  const applied = [];
  const errors = [];
  // reducers.jsx groupBy: every dispatch below collapses into ONE history entry (one Undo).
  const prev = window.cropUndoGroup;
  window.cropUndoGroup = `t3-${Date.now()}`;
  try {
    list.forEach((op, index) => {
      try {
        applied.push({ index, ids: applyOne(dispatch, getState, op || {}, navId) });
      } catch (e) {
        errors.push({ index, message: String((e && e.message) || e).slice(0, 200) });
      }
    });
  } finally {
    window.cropUndoGroup = prev ?? null;
  }
  return { applied, errors };
}

// contract projection for tests, not for production use
/**
 * Contract projection (G7) — what server and client must agree on after the same op batch:
 * the scene background and every box in z-order, in px / seconds, with the parsed text style.
 */
export function projectScene(present, navId) {
  const nav = present.navItemsById[navId];
  const vt = (present.viewToolbarsById || {})[navId] || {};
  const sec = (f) => +((Number(f) || 0) / FPS).toFixed(2);
  return {
    background: vt.background || null,
    boxes: (nav.boxes || []).map((id) => {
      const b = present.boxesById[id] || {};
      const tb = (present.pluginToolbarsById || {})[id] || {};
      const tl = b.timeline || {};
      const t = tb.pluginId === "BasicText" ? parseTextStyle(tb.state && tb.state.__text) : null;
      return {
        id,
        ...boxRect(present, id),
        start: sec(tl.startFrame),
        end: sec(tl.endFrame),
        hidden: tl.visible === false,
        enter:
          (b.boxAnimation && b.boxAnimation.animation && b.boxAnimation.animation.name) || null,
        exit:
          (b.boxExitAnimation &&
            b.boxExitAnimation.animation &&
            b.boxExitAnimation.animation.name) ||
          null,
        text: t && {
          content: t.content,
          color: t.color,
          fontFamily: t.fontFamily,
          fontSizePx: t.fontSizePx,
          weight: t.weight,
          align: t.align,
        },
      };
    }),
  };
}
